
import Raphael from 'raphael';
import { ElemHomeView } from './ElemHomeView';
import { ElemBigView } from './ElemBigView';
import { ElemPlayView } from './ElemPlayView';
import ElemAdornView from "./ElemAdornView";
import { ElemEditableView } from "./editable/ElemEditableView";
import { UndoHelper } from '../dynDataSet/undoManager';
import DynG from "../../utils/Global";

class ElemUndoer extends UndoHelper {
    constructor( Dyn, elemPlayContainer ) {
        super( Dyn, elemPlayContainer );
        this.Dyn = Dyn;
        this.elemPlayContainer = elemPlayContainer;
        if( Dyn.debugging ) { // for testing undo/redo from the console, before the UI is ready
            DynG.testUndo = () => {
                this.Dyn.undoManager.undo();
            };
            DynG.testRedo = () => {
                this.Dyn.undoManager.redo();
            };
        }
    }

    undo( undoableData ) {
        undoableData.undo( undoableData );
    }

    /**
     * Redo an action. Note that redo() can simply call the original "do" method (or not)
     * but must also include a call to Dyn.undoManager.saveUndoStep() just like the original "do" method,
     * to make the redo also undoable.
     * @param undoableData
     */
    redo( undoableData ) {
        undoableData.redo( undoableData );
    }

    /** handle undo of a state change, like groupBy or orderBy,
     * by using the save() and restore() methods.
     * This works for most Play View actions, other than data edits, where the container state is small.
     * @param actionID -- unique ID for the action, like "groupBy" or "orderBy"
     * @param saveData -- data from ElemPlayContainer.save() before changes were made to the state
     * @param actionString -- string to display in the undo/redo menu
     */
    saveUndoOfState( actionID, saveData, actionString ) {
        this.Dyn.assert( saveData.cards, "saveUndoOfState: saveData should have .cards, from ElemPlayContainer.save()" );
        let undoStep = {
                saveData: saveData,
                restoreData: null, // to be captured in undo func
                actionID: actionID,
                actionString: actionString,
                undo: ( undoableData ) => {
                    undoableData.restoreData = this.elemPlayContainer.save(); // save for future redo
                    this.elemPlayContainer.restore( undoableData.saveData ); // undo it
                },
                redo: ( undoableData ) => {
                    this.elemPlayContainer.restore( undoableData.restoreData ); // redo it
                    this.saveUndoOfRedo( undoableData ); // save for future undo of redo
                }
            };
        // save another undo step since restore does not create an undo step
        this.Dyn.undoManager.saveUndoStep( undoStep, this, false /* isUndoOfRedo */ );
    }

    saveUndoOfAdorns( actionID, saveData, actionString ) {
        // save an undo step for the adornments, like annotations
        // assumes that there was no change at all to the rest of the play state (otherwise caller should use saveUndoOfState())
        this.Dyn.assert( saveData.plotShaper, "saveUndoOfAdorns: saveData should have .plotShaper, from ElemAdornsView.save()" );
        let undoStep = {
                saveData: saveData,
                restoreData: null, // to be captured in undo func
                actionID: actionID,
                actionString: actionString,
                undo: ( undoableData ) => {
                    undoableData.restoreData = this.elemPlayContainer.adornsView.save(); // save for future redo
                    this.elemPlayContainer.adornsView.restore( undoableData.saveData ); // undo it
                },
                redo: (undoableData) => {
                    this.elemPlayContainer.adornsView.restore( undoableData.restoreData ); // redo it
                    this.saveUndoOfRedo( undoableData ); // save for future undo of redo
                },
            };
        this.Dyn.undoManager.saveUndoStep( undoStep, this, false /* isUndoOfRedo */ );
    }

    saveUndoOfPlotElement( undoStep, canUpdateCallBack ) {
        // save an undo step for the adornments, like annotations
        // assumes that there was no change at all to the rest of the play state (otherwise caller should use saveUndoOfState())
        this.Dyn.assert( undoStep.saveData.primaryType, "saveUndoOfAdorns: saveData should have .saveData.primaryType, from PlotElement.save()" );
        undoStep.undo = ( undoableData ) => {
                this.elemPlayContainer.adornsView.restorePlotElement( undoableData.saveData ); // undo the plot element change
            };
        undoStep.redo = (undoableData) => {
                this.elemPlayContainer.adornsView.restorePlotElement( undoableData.restoreData ); // redo the plot element change
                this.saveUndoOfRedo( undoableData ); // save for future undo of redo
            };
        this.Dyn.undoManager.saveOrUpdateLastUndoStep( undoStep, this, canUpdateCallBack ); // if callback provided, combine sequential undo steps
    }

    // saveUndoOfOptions( actionID, saveData, restoreData, actionString ) {
    // }

    saveUndoOfRedo( undoableData ) {
        // after redo, we must create another undo step just like the original "do" method
        this.Dyn.undoManager.saveUndoStep( {
            saveData: undoableData.saveData,
            restoreData: undoableData.restoreData,
            actionID: undoableData.actionID,
            actionString: undoableData.actionString,
            undo: undoableData.undo,
            redo: undoableData.redo
        }, this, true /* isUndoOfRedo */ );
    }
}

export default class ElemPlayContainer {

    static bigCardsOnLeft = false; // disable optional & experimental layout where big cards are always on the left

    constructor( Dyn, dataSet, plotObj, isEditable, onStateChange ) {

        // check for URL parameter &bigCardsOnLeft or &bigCardsOnLeft=true
        const urlParams = new URLSearchParams(window.location.search);
        const bcol = urlParams.get('bigCardsOnLeft');
        ElemPlayContainer.bigCardsOnLeft = (typeof bcol==="string") && (bcol !== 'false');
        Dyn.debugging && console.log( "&bigCardsOnLeft="+ElemPlayContainer.bigCardsOnLeft );

        // references to other objects
        this.Dyn = Dyn;
        this.Dyn.elemPlayContainer = this;
        this.dataSet = dataSet; // reference to Dyn.DataSet
        this.plotObj = plotObj;
        this.onStateChange = onStateChange;
        this.subscriptions = [];
        this._originalPlotState = null; // saved plot state for reloadable plots, see PlotView

        // our data
        this.undoer = new ElemUndoer( Dyn, this );
        this.options = {}; // isIconOnly, etc
        this.state = {}; // where are the cards? home? in big cards? in play area?
        this.sizes = {};
        // this.bounds = new Dyn.mathUtil.Rect(0,0,0,0); // position of paper
        this.paper = null;	// Rafael canvas
        this.defaultTipAttributes = this.dataSet.getTooltipAttributes(); // from metadata
        if( ! this.defaultTipAttributes.length ) {
            this.defaultTipAttributes = [ // or default to first attribute other than "Case"
                this.dataSet.attribs.find((attr)=>!attr.isCaseAttribute)
            ].filter( attr => !!attr );
        }
        this.isEditable = !!isEditable;
        this.isEditing = false;

        // subviews (overlapping areas drawing on .paper within .bounds
        this.home = new ElemHomeView( Dyn, this ); // manages small cards and area for small card outlines
        this.big = new ElemBigView( Dyn, this ); // manages big cards and area for big cards
        this.play = new ElemPlayView( Dyn, this ); // manages play area and orderby/groupby plots
        this.editableView = null; // manages editable cards; created/destroyed by updateEditState()
        this.adornsView = new ElemAdornView( Dyn, this ); // manages mini plot view for Annotations

        this.setState( this.big.getBigCardState());
    }

    destroy() {
        // Remove subscribes
        this.Dyn.mediatorLocalRemoveAll(this.subscriptions);

        // destroy sub-objects
        this.home.destroy(); this.home = null;
        this.big.destroy(); this.big = null;
        this.play.destroy(); this.home = null;
        this.editableView?.destroy(); this.editableView = null;
        this.adornsView.destroy(); this.adornsView = null;

        // destroy SVG layer in view
        this.Dyn.plotUtil.destroyPaperAndSVGObjects( this.paper );
        this.paper = null;

        // remove references
        let Dyn = this.Dyn;
        this.Dyn = null;
        this.dataSet = null;
        this.plotObj = null;
        this._originalPlotState = null;

        // notify dependent objects
        Dyn.mediator.publish("elemPlayContainer:destroy");
    }

    // Initialize the plot using dataSet
    initVisuals( sizes, plotState=null ) {
        let Dyn = this.Dyn;
        this.sizes = Object.assign({},sizes);
        this.sizes.containerWidth = this._minZero( this.plotObj.width());
        this.sizes.containerHeight = this._minZero( this.plotObj.height());

        // create the plotting area
        this.paper = Raphael( this.plotObj[0], this.sizes.containerWidth, this.sizes.containerHeight );
        this.home.initVisuals();
        this.big.initVisuals();
        this.play.initVisuals();
        this.adornsView.initVisuals();

        this.updateEditState();

        this._resizeViews();

        // initialize the tooltip utility (one per plot, which gets positioned over any one element on hover-over)
		Dyn.toolTip = new Dyn.ToolTip( this.paper, this.plotObj );

        // handle plot resize
        Dyn.mediatorLocalSubscribe( this.subscriptions, "plot:containerResized", (args) => this.handlePlotResize( args ) );
        Dyn.mediatorLocalSubscribe( this.subscriptions, "pref:newTheme", (args) => this.handleColorThemeChange( args.themeName, args.subTheme ) );
        Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setAttributeMetadata", (args) => this.handleUpdatedAttribute( args ));
        // Update home view when case values are updated
        if(this.isEditable) {
            Dyn.mediatorLocalSubscribe( this.subscriptions, "records:setAttributeValues", (args) => this.home?.updateVisuals());
        }

        this.restoreFromPlotState( plotState ); // restore saved state if any
    }

    // create or destroy the editable view, when this.isEditing changes
    updateEditState() {
        if( this.isEditing && ! this.editableView ) {
            this.editableView = new ElemEditableView( this.Dyn, this );
            this.editableView.initVisuals();
            this._resizeViews();
        } else if( ! this.isEditing && this.editableView ) {
            this.editableView.destroy();
            this.editableView = null;
        }
        return Boolean( this.editableView );
    }

    setEditModeOn(caseIndex) {
        // save current state before editing
        this.stateToRestoreAfterEdit = this.save();
        this.resetPlay(); // clear plot and home
        this.isEditing = true;
        this.Dyn.mediator.publish("elemPlayContainer:editModeOn");
        // Open attribute view
        this.handleUpdatedAttribute({caseIndex: caseIndex});
    }

    setEditModeOff() {
        this.isEditing = false;
        this.Dyn.mediator.publish("elemPlayContainer:editModeOff");
        // Close attribute view
        this.handleUpdatedAttribute();
        // restore state after editing
        if(this.stateToRestoreAfterEdit) {
            this.restore(this.stateToRestoreAfterEdit);
            this.stateToRestoreAfterEdit = null;
        }
    }

    updateVisuals(args) {
        if( this.updateEditState()) {
            this.editableView.updateVisuals(args?.caseIndex);
        }
        this.play.updateVisuals();
        this.home.updateVisuals();
        this.big.updateVisuals(); // do after play.updateVisuals() to restore cards in big cards while plotting
        this.adornsView.updateVisuals();
    }

    handleColorThemeChange( themeName, subTheme ) {
        this.Dyn.plotUtil.setThemeColors( themeName, subTheme );
        this.updateVisuals();
    }

    handlePlotResize( args ) {
        // reset the SVG canvas/paper
        this.sizes.containerWidth = this._minZero( this.plotObj.width() );
        this.sizes.containerHeight = this._minZero( this.plotObj.height() );
        if( args.cardAreaWidth > 0 ) {
            this.sizes.cardAreaWidth = args.cardAreaWidth;
        }
        this._resizeViews();
        this.updateVisuals();
    }

    handleUpdatedAttribute( args ) {
        this.updateVisuals(args); // rebuild plot and home with new attribute
    }

    _resizeViews() {
        let Dyn = this.Dyn,
            playNBigLeft = this.sizes.cardAreaWidth+this.sizes.cardPlayBorder,
            smallCardArea = new Dyn.mathUtil.Rect( 0, 0,
                this.sizes.cardAreaWidth, this.sizes.containerHeight ),
            bigCardArea = new Dyn.mathUtil.Rect(
                this.sizes.cardAreaWidth+this.sizes.cardPlayBorder, 0,
                this._minZero( this.sizes.containerWidth - playNBigLeft ),
                this._minZero( this.sizes.containerHeight - this.sizes.bottomToolbarHeight )),
            adornsPlotArea = new Dyn.mathUtil.Rect( 0, 0,
                this.sizes.containerWidth, this.sizes.containerHeight ),
            bigCardsInPlotArea = smallCardArea;
        if( ElemPlayContainer.bigCardsOnLeft ) {
            let leftPanel = smallCardArea;
            smallCardArea = bigCardArea;
            bigCardArea = leftPanel;
            bigCardsInPlotArea = leftPanel;
        }
        this.paper.setSize( this.sizes.containerWidth, this.sizes.containerHeight );
        this.home.resize( smallCardArea );
        this.play.resize( new Dyn.mathUtil.Rect(
            playNBigLeft, 0,
            this._minZero( this.sizes.containerWidth - playNBigLeft ),
            this._minZero( this.sizes.containerHeight - this.sizes.bottomToolbarHeight )));
        this.big.resize( bigCardArea, bigCardsInPlotArea );
        this.editableView?.resize( bigCardArea, bigCardsInPlotArea );
        this.adornsView.resize( adornsPlotArea );

        this.layerElements();
    }
    _minZero( n ) { return Math.max(0,n); }

    /** state of the Play area. Coordinate the state options with:
     * ElemHomeView.getHomeState(),
     * ElemBigView.getBigCardState(),
     * this.groupBy(), this.orderBy()
     * @param state
     */
    setState( state ) {
        const lastPlot = this.state?.plot;
        this.Dyn.assert( state, "Play state should not be empty" );

        switch( state?.plot ) {
            case "home":
            case "leftHome":
            case "bigCards":
                this.options = {};
                this.state = state; // set new state; clear old state
                this.play.clearTitles();
                break;
            case "orderBy":
            case "groupBy":
                // set new state, but preserve options like isSpreadOut, isIconOnly, isShowingCount
                this.state = state;
                break;
            default:
                this.state = { plot:"home" };
                this.Dyn.assert( "undefined play state: "+state?.plot.toString() );
        }
        // When plot is changed, refresh react parent component
        if(lastPlot != this.state.plot) {
            this.onStateChange({state: this.state});
        }
        this.Dyn.debugUndo && console.log( "ElemPlayContainer.setState", state, "options:", this.options,
            "onStateChange:"+((lastPlot != this.state.plot) ?"true":"false") );
    }

    /* API for react control states */
    get canShowPlotControls() {
        return [ "bigCards", "orderBy", "groupBy" ].includes( this.state?.plot );
    }

    /** show the action buttons used by OrderBy or GroupBy plots? */
    get canShowActionButtons() {
        return ["groupBy", "orderBy"].includes( this.state?.plot );
    }

    /** disable (grey out) the Reset button? */
    get canDisableResetButton() {
        return ["home"].includes( this.state?.plot );
    }

    /** disable (grey out) the Plot View button? */
    get canDisablePlotViewButton() {
        let isOrderOrGroupBy = ["groupBy", "orderBy"].includes( this.state?.plot ),
            hasNoCards = (0 === this.dataSet.getNumCasesAfterFilter());
        // switch to plot view ONLY if state is OrderBy or GroupBy,
        // EXCEPT if user has filtered out all cases, then Reset, because at that point there is no card to drag,
        // 	and no way to make a plot. The workaround is to enable the Plot View Button so they can change filtering again.
        return isOrderOrGroupBy || hasNoCards;
    }

    /** save the play data for later .restore() */
    save() {
        let data = {
            plot: this.state.plot,
            options: Object.assign({}, this.options),
            cards: this.home.saveCards(),
            editing: this.editableView?.saveEditState(),
            adorns: this.adornsView.save() // plot adornments/annotations
        }
        if( this.state.attribute ) {
            data.attributeID = this.state.attribute.id;
        }
        if( this.state.cases ) {
            this.Dyn.assert( this.state.plot==='bigCards');
            data.bigCards = { cases: this.state.cases };
        } else if( this.hasPlot()) {
            let bigCardCasesInPlot = this.big.getBigCardCases();
            if( bigCardCasesInPlot.flat().length > 0 ) {
                data.bigCards = { cases: bigCardCasesInPlot };
            }
        }
        return data;
    }

    /** restore the play container from the results of a previous .save() */
    restore( data ) {
        let Dyn = this.Dyn,
            attribute = this.dataSet.getAttributeByID( data?.attributeID ); // null if data missing

        // reset any current setting first
        this.play.resetPlay(); // clear old plot, if any
        this.big.resetBigCards( false ); // reset to non-plot, non-open state
        this.home.resetSmallCards(); // reset to home state

        // restore options
        let optionsChanged = false;
        ['isSpreadOut', 'isIconOnly', 'isShowingCount'].forEach((option) => {
            let newOption = Boolean( data?.options?.[option] );
            if( newOption !== Boolean( this.options[option]) ) {
                this.options[option] = newOption;
                optionsChanged = true;
            }
        });
        if( optionsChanged ) {
             this.onStateChange({ options: this.options }); // notify react parent to update button state
        }

        // edit state next, so home/big views can use it
        this.isEditing = Boolean( data?.editing?.isEditing );
        if( this.updateEditState())
            this.editableView.restoreEditState( data?.editing );

        // restore plot, big cards, dragged cards ("leftHome" state)
        switch( data?.plot ) {
            case "groupBy":
            case "orderBy":
                this.play.restorePlot( data?.plot, attribute );
                this.home.restoreCards( data?.cards ); // restore any card custom/dragged position
                this.big.restoreBigCards( data?.bigCards ); // do last to restore big cards while plotting
                Dyn.assert( this.hasPlot());
                break;
            case "bigCards":
                this.big.restoreBigCards( data?.bigCards );
                this.home.restoreCards( data?.cards ); // restore any card custom/dragged position
                Dyn.assert( this.state.plot==="bigCards");
                break;
            case "home":
            default:
                this.home.restoreCards( data?.cards );
        }
        Dyn.assert( this.state.plot === data.plot );
        this.adornsView.restore( data?.adorns);
        this.layerElements();
        Dyn.debugging && console.log( this.state.plot, this.options );
    }

    // save the plot state, for when PlotView.save() is not possible
    savePlotState() {
        return {
            playState: this.save(),
            dataSet: this.dataSet.save() // save data modifications, like PlotState.save()
        };
    }

    // restore the plot state to a play view, for when PlotView.restore() is not possible
    restoreFromPlotState( plotState ) {
        // first restore dataset modifications, just like PlotView.restore()
        if( plotState?.dataSet ) {
            this.dataSet.restore( plotState.dataSet, true /* deferPlotUpdate */ );
            this.home.updateVisuals(); // make filter changes from dataset if needed
            this.play.updateFilterDisplay();
        }
        // then restore play state
        if( plotState?.playState ) {
            this.restore( plotState.playState ); // restore from play state
        } else if ( plotState?.plotShaper ) {
            this.restore( this.switchIntoPlayView( plotState )); // or from plot state
        }
    }

    /** return true if Play View state is in any type of plot, or optionally just the one plot type specified */
    hasPlot( optionalPlotType) {
        if( optionalPlotType ) {
            return this.state?.plot===optionalPlotType;
        }
        return this.state?.plot==="orderBy" || this.state?.plot==="groupBy";
    }

    groupBy( attr, options ) {
        let Dyn = this.Dyn,
            undoData = (options?.noUndo ? null : this.save());
        this.setState({ plot:"groupBy", attribute:attr });
        if( ElemPlayContainer.bigCardsOnLeft )
            this.home.showHideOutlines( false );
        this.big.arrangeBigCards( this.hasPlot());
        this.big.hideBigCardsAndOutline();
        this.play.groupBy( this.dataSet, attr, this.home.smallCards  );
        if( undoData ) {
            this.undoer.saveUndoOfState( 'groupBy', undoData, Dyn.loc( "Group By" ) );
        }
    }

    orderBy( attr, options ) {
        let Dyn = this.Dyn,
            undoData = (options?.noUndo ? null : this.save());
        this.setState({ plot:"orderBy", attribute:attr });
        if( ElemPlayContainer.bigCardsOnLeft )
            this.home.showHideOutlines( false );
        this.big.arrangeBigCards( this.hasPlot());
        this.big.hideBigCardsAndOutline();
        this.play.orderBy( this.dataSet, attr, this.home.smallCards );
        if( undoData ) {
            this.undoer.saveUndoOfState( 'orderBy', undoData, Dyn.loc( "Order By" ) );
        }
    }

    onStackClick() {
        let Dyn = this.Dyn,
            undoData = this.save();
        this.options.isSpreadOut = !this.options.isSpreadOut;
        this.big.removeAnimateCardsFromAllBigCards();
        this.play.updateVisuals( false );
        this.undoer.saveUndoOfState( 'spread', undoData, Dyn.loc( "Spread" ) );
    }

    onIconsClick() {
        let Dyn = this.Dyn,
            undoData = this.save();
        this.options.isIconOnly = !this.options.isIconOnly;
        this.home.setCardsIconOnly( this.options.isIconOnly );
        this.big.removeAnimateCardsFromAllBigCards();
        this.play.updateVisuals( false );
        this.undoer.saveUndoOfState( 'icons', undoData, Dyn.loc( "Icons" ) );
    }

    onCountClick() {
        let Dyn = this.Dyn,
            undoData = this.save();
        this.options.isShowingCount = !this.options.isShowingCount;
        this.big.removeAnimateCardsFromAllBigCards();
        this.play.updateVisuals( false );
        this.undoer.saveUndoOfState( 'count', undoData, Dyn.loc( "Count" ) );
    }

    resetPlay( options ) {
        let Dyn = this.Dyn,
            undoData = (options?.noUndo ? null : this.save());
        // this.setState({ plot:"home" });
        // this.options.isIconOnly = false; // undo isIconOnly
        this.play.resetPlay(); // undo bins etc.
        this.big.resetBigCards( false ); // don't animate home yet
        if( ElemPlayContainer.bigCardsOnLeft )
            this.home.showHideOutlines( true );
        this.home.resetSmallCards(); // animate cards home
        this.updateHomeState();
        this.layerElements(); // undo layering changes from order-by
        Dyn.toolTip.hide(); // fix bug where tooltip persists on firefox after drop
        Dyn.assert( this.state.plot==="home" && !this.options.isIconOnly );
        if( undoData ) {
            this.undoer.saveUndoOfState( 'resetPlay', undoData, Dyn.loc( "Reset Play" ) );
        }
    }

    startStopAnnotationClick( options ) {
        let Dyn = this.Dyn,
            undoAdornsViewData = (options?.noUndo ? null : this.adornsView.save()),
            oldPlotElement = this.adornsView.plotShaper.findElement( Dyn.EPlotControl.kControlsAnnotations ),
            newPlotElement = new options.constructor( Dyn, options.controlType, null );
        Dyn.assert( options.method==='annotate' );
        const createInitialShape = true; // ( args.eventType==='keypress' )
        if( createInitialShape ) {
            // create the initial shape of the new object, then add to the plot
            newPlotElement.createInitialShapeForKeypress( oldPlotElement?.objects?.length || 0 );
            this.adornsView.addPlot( newPlotElement  ); // add to and replace oldPlotElement
            let adornments = this.adornsView.adornments.getAdornmentsArray( newPlotElement.primaryType );
            newPlotElement.doAfterAddingPoints( adornments, this.adornsView.xAxis, this.adornsView.yAxis );
        } else {
            // TODO: if we are not creating the initial shape, then we need to add code for draw tool to start drawing
            // add the new object to the plot, without points, and start the draw tool
            this.adornsView.addPlot( newPlotElement  ); // add to and replace oldPlotElement
        }
        if( undoAdornsViewData ) {
            let actionString = Dyn.loc( newPlotElement.getUndoAction({}));
            this.undoer.saveUndoOfAdorns( 'addAnnotation', undoAdornsViewData, actionString );
        }
        // _gaq.push(['_trackEvent', 'Plot Sketch', 'Click', menuValue]);
    }

    clearAllAnnotations( options=null ) {;
        let Dyn = this.Dyn,
            undoAdornsViewData = (options?.noUndo ? null : this.adornsView.save());
        const controlsAnnotations = Dyn.EPlotControl.kControlsAnnotations;
        if( this.adornsView.plotShaper.hasElement( controlsAnnotations ) ){
            this.adornsView.removePlot( controlsAnnotations, false, Dyn.loc("Clear All Annotations"));
            if( undoAdornsViewData ) {
                this.undoer.saveUndoOfAdorns( 'clearAllAnnotations', undoAdornsViewData, Dyn.loc( "Clear All Annotations" ) );
            }
        }
    }

    layerElements() {
        let playLayersCards = this.hasPlot();
        this.home.layerElements( !playLayersCards, 0 ); // Home 0th layer
        this.big.layerElements();
        this.home.layerElements( !playLayersCards, 1 ); // Home 1st layer
        this.play.layerElements( playLayersCards );
        this.adornsView.layerElements();
    }

    /** remove a small card (e.g. if filter changed), make sure it is removed everywhere */
    removeHideCard( smallCard ) {
        //this.home.removeHideCard( smallCardsIndex );
        this.big.removeHideCard( smallCard );
        this.play.removeHideCard( smallCard );
        smallCard.destroy();
    }

    /** Handle a request to switch out of Play view and into Elementary Plot View
     * returns true if it is OK to switch, plus a plot state similar to the play view
     */
    switchOutOfPlayView() {
        // note: we must get a valid state even if not in OrderBy or GroupBy state, for example if there is a filter that removes all cards; see canDisablePlotViewButton()
        let state = {
            canSwitch: true, // Note: no longer used, consider removing in future versions --CM 6/24/2024.
            playState: this.save(),
            plotState: {
                // PLOT STATE: to match play state
                // for now, we assume that we only need to specify options that are different from the initial, mixed-up plot
                "xAxisView": {},
                "yAxisView": {
                    "titleString": ""
                },
                "topView": {},
                "legendView": {},
                "plotShaper": {
                    "iconStyler": this.home.iconStyler.save(),
                    "plotElements": [],
                },
                "dataSet": {}
            },
            "version": 2
        };
        // add plot elements
        let plotElements = state.plotState.plotShaper.plotElements;
        if( this.hasPlot()) {
            plotElements.push( { "primaryType": "controlsDotPlot" } );
            if( this.options.isShowingCount ) {
                plotElements.push( {"primaryType":"controlsCount","controlsCellCount":true} );
            }
        } else {
            plotElements.push( { "primaryType": "controlsMixup" } );
        }
        // add OrderBy/GroupBy attribute as the X-axis and Legend attribute
        let dataSet = state.plotState.dataSet,
            xAxisView = state.plotState.xAxisView;
        if( this.state.attribute?.id ) {
            xAxisView.attributeID = [ this.state.attribute.id ];
            dataSet.selectedAttributeId =
                dataSet.orderByAttributeId = this.state.attribute.id;
        }
        // add cases in big cards as the selected/Case Card case
        let bigCardCases = this.state.cases?.flat() || this.big.getBigCardCases().flat() || [];
        if( bigCardCases.length > 0 ) {
            dataSet.selectedCases = bigCardCases;
        }
        return( state );
    }

    /** switch from a plot view to a play view, converting the plot types where possible */
    switchIntoPlayView( plotState ) {
        let playState = {};
        if( plotState?.plotShaper ) {
            // convert the plot view
            let attributeIDs = plotState?.xAxisView?.attributeID,
                plotElements = plotState?.plotShaper?.plotElements,
                attribute = this.dataSet.getAttributeByID( attributeIDs ? attributeIDs[0] : "" ),
                dotPlot = plotElements.find((el) => el.primaryType==="controlsDotPlot"),
                count = plotElements.find((el) => el.primaryType==="controlsCount"),
                selectedCases = plotState?.dataSet?.selectedCases || [];
            if( attribute && dotPlot ) {
                // convert to OrderBy or GroupBy (with optional Big Cards)
                playState = {
                    plot: attribute.stats.isNumeric() ? "orderBy" : "groupBy",
                    attributeID: attribute.id,
                    options: { isShowingCount: Boolean(count) }
                };
                if( selectedCases.length > 0 ) {
                    playState.bigCards = { cases:[] };
                    selectedCasesToBigCards( selectedCases, playState, true );
                }
            } else {
                if( selectedCases.length > 0 ) {
                    // convert to Big Cards
                    playState = { plot:"bigCards", bigCards:{ cases:[] }};
                    selectedCasesToBigCards( selectedCases, playState, false );
                } else {
                    // convert to cards in home state
                    playState = { plot: "home" };
                }
            }
        }
        return playState;

        function selectedCasesToBigCards( selectedCases, playState, isPlotting ) {
            if( Number.isFinite(selectedCases[0]) && (ElemBigView.numBigCardsInPlot > 0 || !isPlotting))
                playState.bigCards.cases.push( [selectedCases[0]] );
            if( Number.isFinite(selectedCases[1]) && (ElemBigView.numBigCardsInPlot > 1 || !isPlotting))
                playState.bigCards.cases.push( [selectedCases[1]] );
        }
    }

    /** respond to the start of the animation after dropping a small card */
    dropAnimationStart( dragResult ) {
        if( dragResult.result==="big card") { // if small card dropped onto a big card
            this.big.dropAnimationStart( dragResult );
        }
    }

    /** respond to the end of the animation after dropping a small card */
    dropAnimationEnd( dragResult ) {
        if( dragResult.result==="big card") { // if small card dropped onto a big card
            this.big.dropAnimationEnd( dragResult );
            //this.setState( this.big.getBigCardState()); issue 2197, set state on dropAnimationStart
        }
        this.updateHomeState();
        this.layerElements(); // undo layering changes from drag
    }

    /** check to see if cards are or are not home; update state settings to match
     * obsolete: use updateNonPlotState() instead */
    updateHomeState() {
        switch( this.state.plot ) {
            case "home":
            case "leftHome":
                this.setState( this.home.getHomeState());
            // else do nothing
        }
    }

    /** check to see if the plot state is correct, and update it if not */
    updateNonPlotState() {
        if( this.play.isInOrderByOrGroupBy() ) {
            // has bins for plot, check state, because only this.GroupBy() or this.OrderBy() can set a plot state
            this.Dyn.assert( this.hasPlot(), "updateNonPlotState: has GroupBy or OrderBy but state incorrect:"+this.state?.plot );
        } else {
            // else set state to "bigCards" (1+ cards open in big cards)
            // or "leftHome" (0 cards in big cards and 1+ cards dragged away from home)
            // or "home" (all cards in home area)
            this.setState( this.big.getBigCardState() );
            this.Dyn.assert( ["home", "leftHome", "bigCards"].includes( this.state.plot ));
        }
    }

    fastAnimation() {
        return {
            time: 700, // slower than Dyn.plotUtil.quickAnimationTimeMs
            easing: this.Dyn.plotUtil.quickAnimationEasing
        }
    }

    slowAnimation() {
        return {
            time: this.Dyn.plotUtil.stdAnimationTimeMs,
            easing: this.Dyn.plotUtil.stdAnimationEasing
        }
    }

    noAnimation() {
        return {
            time: 0,
            easing: this.Dyn.plotUtil.stdAnimationEasing
        }
    }

    iconColor() {
        return this.Dyn.plotUtil.kCaseColor;
    }

    getCardToolTip( caseIndex, smallCardIndex ) {
        let Dyn = this.Dyn,
            tip = "",
            tipAttributes = [],
            caseAttribute = this.dataSet.getAttributeByID('id');
        [ ...this.defaultTipAttributes, this.state.attribute, caseAttribute ].forEach((attr) => {
            if( attr && ! tipAttributes.includes( attr ))
                tipAttributes.push( attr );
        });
        tipAttributes.forEach((attr) => {
            if( tip ) {
                tip += "\n";
            }
            tip += Dyn.loc("{{attribute}}: {{value}}", {
                attribute: (attr.isCaseAttribute ? Dyn.loc("Card") : attr.name ),
                value: (attr.isCaseAttribute ? (smallCardIndex+1).toString() : attr.caseValueToString( caseIndex ))
            });
        });
        return tip;
    }

    getImagePasteHandler( imageClipboardType ) {
        let plotElement = this.adornsView.plotShaper.findElement( this.Dyn.EPlotControl.kControlsAnnotateImage);
        return plotElement?.getPasteHandler( imageClipboardType );
    }

    /** is a focusable element within the plot the current keyboard focus (document.activeElement?).
     * Used to help the paste handlers avoid overriding paste for non-plot objects in the document. */
    hasKeyboardFocus( activeElement ) {
        return( this.plotObj.has(activeElement).length > 0 ); // true if activeElement has plot container as an ancestor
    }

    setBackgroundFocus() {
        // set the plot wrapper as focus so hasKeyboardFocus() returns true,
        // to help the paste handlers avoid overriding paste for non-plot objects in the document.
        // this.Dyn.plotUtil.setAsKeyboardFocus(this.backgroundRect);
    }

    saveOriginalPlotState( plotState ) {
        // save the original plot state for reloadable plots, see PlotView
        this._originalPlotState = plotState;
    }

    getOriginalPlotState() {
        // return the original plot state for reloadable plots, see PlotView
        return this._originalPlotState;
    }
}

